import { TFunction } from 'i18next';
import { Target } from 'shared/assets/icons';
import { Option } from 'shared/components/ui/custom-form-multi-select';
import { FilterOptionType, FilterSelectOption } from 'shared/types/filter';
import { FilterOptionKeys } from 'shared/types/filter-document';
import { Goal } from 'shared/types/goal';
import { goalOtherID } from 'shared/types/orderable-section';
import { getSubGoals } from 'shared/utils/get-sub-goals';
import { sortGoals } from 'shared/utils/sort-goals';

export type Options = {
  t: TFunction;
  hideNoGoal?: boolean;
};

const buildGoalOptions = (goal: Goal, allGoals: Goal[]): Option => {
  const childGoals = sortGoals(getSubGoals(goal.id, { goals: allGoals }), {
    allGoals,
    sortMode: goal.goalSortingMode,
    customOrder: goal.goalSorting,
  });

  const children = childGoals.map((childGoal) =>
    buildGoalOptions(childGoal, allGoals),
  );

  return {
    label: goal.name,
    value: goal.id,
    children: children.length ? children : undefined,
  };
};

export const createGoalFilter = (
  goals: Goal[],
  { t, hideNoGoal }: Options,
): FilterSelectOption[] => {
  if (!goals.length) {
    return [];
  }

  const goalIds = new Set(goals.map((g) => g.id));
  const mainGoals = goals.filter((goal) => {
    if (!goal.parentIds?.length) {
      return true;
    }

    const parentId = goal.parentIds[goal.parentIds.length - 1];
    return !goalIds.has(parentId);
  });
  const groupedOptions = mainGoals.map((goal) => buildGoalOptions(goal, goals));

  if (!groupedOptions.length) {
    return [];
  }

  return [
    {
      id: FilterOptionKeys.Goal,
      label: t('context-menus.filtering.task.filters.goals.title'),
      placeholder: t('general.labels.select'),
      icon: Target,
      type: FilterOptionType.MultiSelect,
      selectOptions: [
        ...groupedOptions,
        ...(hideNoGoal
          ? []
          : [
              {
                label: t('general.sections.goals.other.title'),
                value: goalOtherID,
              },
            ]),
      ],
      recursiveSelection: true,
      canReset: true,
      enableSearch: true,
    },
  ];
};
